// Client-side action registry consumed by the ⌘K palette (ADV-805) and any
// other surface that wants to invoke the same logical operation as the
// sidebar UI. Actions carry their own `run` handler, optional CASL
// `ability` gate, and an optional `when` predicate for entries that are only
// meaningful in certain states (e.g. switch-business excludes the active
// business). The palette renders entries in `group` order and filters out
// any action whose ability or when predicate returns false.

import { useMemo } from "react";
import {
  Building2,
  Keyboard,
  LogOut,
  Moon,
  Settings as SettingsIcon,
  Sparkles,
  Sun,
  SunMoon,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import type { AppAbility } from "@adva/auth";

import { useAuth } from "@/contexts/auth-context";
import { useTheme, type Theme } from "@/contexts/theme-context";
import { useAbility } from "@/hooks/use-ability";
import { useMyBusinesses, useSwitchBusiness } from "@/hooks/use-businesses";

type ActionGroup = "appearance" | "account" | "navigation" | "ai";

type AbilityAction = Parameters<AppAbility["can"]>[0];
type AbilitySubject = Parameters<AppAbility["can"]>[1];

interface AbilityPredicate {
  action: AbilityAction;
  subject: AbilitySubject;
}

export interface ActionDef {
  id: string;
  label: string;
  keywords?: readonly string[];
  icon: LucideIcon;
  group: ActionGroup;
  run: () => void;
  ability?: AbilityPredicate;
  when?: () => boolean;
}

interface UseActionsOptions {
  /**
   * Callback to open the keyboard-shortcuts help panel. The panel is owned
   * by `KeyboardShortcutsProvider`, which already passes a binding into
   * `<CommandPalette>` — the action registry receives the same callback so
   * the palette's "Keyboard shortcuts" entry routes to the panel without
   * the registry needing to know about provider internals.
   */
  onOpenShortcutsHelp?: () => void;
}

/** Label shown for the "Ask Adva" entry. Exported so the palette + Phase 4
 *  placeholder route + any future rename can flip in one place
 *  (`[[project_demo_data_names]]`). */
export const ASK_ADVA_LABEL = "Ask Adva";

/** ADR-0027: ability gate for the "Invite member" entry. The same gate is
 *  applied by the `<AbilityRoute>` around `/settings/members` in `App.tsx`,
 *  so anyone whose role can't open that page won't see this action either. */
const INVITE_MEMBER_ABILITY: AbilityPredicate = {
  action: "update",
  subject: "Account",
};

interface BusinessSummary {
  id: string;
  name: string;
  slug: string;
}

export function useActions(options: UseActionsOptions = {}): ActionDef[] {
  const { onOpenShortcutsHelp } = options;
  const { setTheme } = useTheme();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const ability = useAbility();
  const myBusinesses = useMyBusinesses();
  const switchBusiness = useSwitchBusiness();

  return useMemo<ActionDef[]>(() => {
    const all: ActionDef[] = [];

    // ── appearance ────────────────────────────────────────────────────────
    const themes: Array<{ theme: Theme; label: string; icon: LucideIcon }> = [
      { theme: "light", label: "Switch to light theme", icon: Sun },
      { theme: "dark", label: "Switch to dark theme", icon: Moon },
      { theme: "default", label: "Switch to default theme", icon: SunMoon },
    ];
    for (const { theme, label, icon } of themes) {
      all.push({
        id: `theme:${theme}`,
        label,
        keywords: ["theme", "appearance", "color"],
        icon,
        group: "appearance",
        run: () => setTheme(theme),
      });
    }

    // ── account ───────────────────────────────────────────────────────────
    const businesses = (myBusinesses.data?.businesses ??
      []) as BusinessSummary[];
    const activeBusinessId = myBusinesses.data?.active_business_id;
    for (const biz of businesses) {
      if (biz.id === activeBusinessId) continue;
      all.push({
        id: `account:switch-business:${biz.id}`,
        label: `Switch to ${biz.name}`,
        keywords: ["business", "switch", biz.slug],
        icon: Building2,
        group: "account",
        // The palette closes synchronously before mutate() resolves, so a
        // failed switch (403, network) would leave the user on the old tenant
        // with no feedback. Surface the failure via toast.
        run: () =>
          switchBusiness.mutate(biz.id, {
            onError: () =>
              toast.error(`Could not switch to ${biz.name}. Please try again.`),
          }),
      });
    }

    all.push({
      id: "account:invite-member",
      label: "Invite member",
      keywords: ["invite", "member", "user", "team"],
      icon: UserPlus,
      group: "account",
      run: () => navigate("/settings/members"),
      ability: INVITE_MEMBER_ABILITY,
    });

    all.push({
      id: "account:sign-out",
      label: "Sign out",
      keywords: ["logout", "log out", "exit", "sign out"],
      icon: LogOut,
      group: "account",
      // signOut() returns a promise; the palette has already closed by the
      // time it rejects. Surface a toast so a failed sign-out (revoked
      // session, network) is visible instead of silently leaving the user
      // logged in.
      run: () => {
        signOut().catch(() => {
          toast.error("Could not sign out. Please try again.");
        });
      },
    });

    // ── navigation ────────────────────────────────────────────────────────
    all.push({
      id: "nav:settings",
      label: "Open settings",
      keywords: ["settings", "preferences", "config"],
      icon: SettingsIcon,
      group: "navigation",
      run: () => navigate("/settings"),
    });

    all.push({
      id: "nav:keyboard-shortcuts",
      label: "Keyboard shortcuts",
      keywords: ["shortcuts", "keys", "help"],
      icon: Keyboard,
      group: "navigation",
      run: () => {
        if (onOpenShortcutsHelp) onOpenShortcutsHelp();
      },
      // Hide the entry entirely when no callback was supplied — there is
      // no useful default behavior outside the palette's host provider.
      when: () => typeof onOpenShortcutsHelp === "function",
    });

    // ── ai ────────────────────────────────────────────────────────────────
    all.push({
      id: "ai:ask-adva",
      label: ASK_ADVA_LABEL,
      keywords: ["ai", "assistant", "chat", "ava"],
      icon: Sparkles,
      group: "ai",
      run: () => navigate("/ask-adva"),
    });

    return all.filter((a) => {
      if (a.when && !a.when()) return false;
      if (a.ability && !ability.can(a.ability.action, a.ability.subject)) {
        return false;
      }
      return true;
    });
  }, [
    setTheme,
    signOut,
    navigate,
    ability,
    myBusinesses.data,
    switchBusiness,
    onOpenShortcutsHelp,
  ]);
}
